/**
 * The shared send path for both OPVisual items: build the messages, send them
 * through the OPVisual machine's bridge on the item's channel, and record what
 * was SENT - only once the send came back `ok`, so a refused, failed or
 * unheard send never marks a cell.
 *
 * On the OPBridge channel (DEC-215) `ok` means an OPVisual was attached to that
 * bridge to receive it; `app-not-linked` means none was, and is remembered per
 * machine (`session.ts` link state) so both items can say so on their face.
 */

import { sendOpVisual, type FireResult } from '../opbridge/dispatch.ts';
import { bridgeConnectedReactive, resolveBridge } from '../opbridge/store.ts';
import { pushToast } from '../ui/toast.ts';
import {
  actionMsgs, layerOpacityMsg, layerEnabledMsg, layerBlendMsg, layerSpeedMsg,
  masterOpacityMsg, lookMsg, camShakeMsg, vizMacroMsg, vizVariationMsg, DEFAULT_OPV_VIA,
  type OpVisualAction, type OpvQuant, type OpvBlend, type OpvLookParam, type OpvMsg, type OpvVia,
} from './wire.ts';
import {
  markCellSent, markColumnSent, markLayerCleared, markAllCleared, markPanicked,
  setLayerLocal, setMasterLocal, setLookLocal, setOpvLinkDown, opvLinkDownFor,
} from './session.ts';

/** Where an OPVisual item sends: the ONE machine it is aimed at, and the
 *  channel ("Sends via"; absent = OPBridge). Both come straight off the item. */
export interface OpvAim {
  bridgeIds: string[] | undefined;
  via?: OpvVia | undefined;
}

/** Every send goes through here, so the link state can never be missed by
 *  one control and kept by another. */
async function send(msgs: OpvMsg[], aim: OpvAim, ignorePanicLatch = false): Promise<FireResult> {
  const r = await sendOpVisual(msgs, aim.bridgeIds, { ignorePanicLatch, via: aim.via });
  if ((aim.via ?? DEFAULT_OPV_VIA) === 'opbridge') {
    if (r.kind === 'app-not-linked') setOpvLinkDown(r.target, true);
    else if (r.kind === 'ok') setOpvLinkDown(r.target, false);
  }
  return r;
}

export async function fireOpVisualAction(
  action: OpVisualAction,
  quant: OpvQuant,
  aim: OpvAim,
): Promise<FireResult> {
  const r = await send(actionMsgs(action, quant), aim, action.verb === 'panic');
  if (r.kind !== 'ok') return r;
  switch (action.verb) {
    case 'cell':       markCellSent(action.col, action.layer); break;
    case 'column':     markColumnSent(action.col); break;
    case 'clearLayer': markLayerCleared(action.layer); break;
    case 'stopAll':    markAllCleared(); break;
    case 'panic':      markPanicked(); break;
    // Blackout is a toggle whose current state we never see, so it moves no
    // control: the master slider still shows where it was last put, which is
    // where OPVisual returns to when the blackout is released.
    default: break;
  }
  return r;
}

/** Continuous and toggle controls. The local position moves FIRST (so a drag
 *  tracks the finger), and the send follows through a throttle. */
export function sendLayerOpacity(layer: number, v: number, aim: OpvAim): Promise<FireResult> {
  setLayerLocal(layer, 'opacity', v);
  return sendOne(layerOpacityMsg(layer, v), aim);
}
export function sendLayerEnabled(layer: number, on: boolean, aim: OpvAim): Promise<FireResult> {
  setLayerLocal(layer, 'enabled', on);
  return sendOne(layerEnabledMsg(layer, on), aim);
}
export function sendLayerBlend(layer: number, mode: OpvBlend, aim: OpvAim): Promise<FireResult> {
  setLayerLocal(layer, 'blend', mode);
  return sendOne(layerBlendMsg(layer, mode), aim);
}
export function sendLayerSpeed(layer: number, speed: number, aim: OpvAim): Promise<FireResult> {
  setLayerLocal(layer, 'speed', speed);
  return sendOne(layerSpeedMsg(layer, speed), aim);
}
export function sendMaster(v: number, aim: OpvAim): Promise<FireResult> {
  setMasterLocal(v);
  return sendOne(masterOpacityMsg(v), aim);
}
export function sendLook(param: OpvLookParam, v: number, aim: OpvAim): Promise<FireResult> {
  setLookLocal(param, v);
  return sendOne(lookMsg(param, v), aim);
}

/** SHAKE is held on the grid: `on` at the press, off at the release. */
export function sendCamShake(on: boolean, amount: number, aim: OpvAim): Promise<FireResult> {
  return sendOne(camShakeMsg(on, amount), aim);
}
/** One visualiser knob, live only (OPVisual reports none of them back). */
export function sendVizKnob(macro: number, v: number, layer: number, aim: OpvAim): Promise<FireResult> {
  return sendOne(vizMacroMsg(macro, v, layer), aim);
}
export function sendVizNewTake(layer: number, quant: OpvQuant, aim: OpvAim): Promise<FireResult> {
  return sendOne(vizVariationMsg(layer, quant), aim);
}

function sendOne(m: OpvMsg | null, aim: OpvAim): Promise<FireResult> {
  return send(m ? [m] : [], aim);
}

/**
 * Did the last OPBridge-protocol send to this item's machine find no OPVisual
 * listening? Reactive. Never true on the OSC channel, which cannot tell.
 *
 * NOT an {@link OpvFault}: a fault disables the control, and this must not -
 * the next press is the only thing that re-checks it (see `setOpvLinkDown`).
 */
export function opvNotLinked(aim: OpvAim): boolean {
  if ((aim.via ?? DEFAULT_OPV_VIA) !== 'opbridge') return false;
  const first = (aim.bridgeIds ?? [])[0];
  if (!first) return false;
  return opvLinkDownFor(resolveBridge(first)?.id);
}

/**
 * The one toast path for both items. An identical error inside
 * `REPEAT_QUIET_MS` is dropped: a slider dragged while OPVisual is not linked
 * sends dozens of times a second, and each answer would otherwise stack its
 * own copy of the same sentence over the surface being played. The item's own
 * face keeps saying it for as long as it is true.
 */
const REPEAT_QUIET_MS = 4000;
const lastErrorAt = new Map<string, number>();

export function reportOpv(r: FireResult, okText?: string): void {
  if (r.kind === 'ok') {
    if (okText) pushToast('ok', okText, 1500);
    return;
  }
  const text = `OPVisual: ${r.detail ?? r.kind}`;
  const now = Date.now();
  const last = lastErrorAt.get(text);
  if (last !== undefined && now - last < REPEAT_QUIET_MS) return;
  lastErrorAt.set(text, now);
  pushToast('error', text, 4000);
}

/**
 * Why an OPVisual item cannot send, or null. The order is the content: the
 * machine has to be chosen before its connection means anything.
 */
export type OpvFault = 'no-machine' | 'offline';

export function opvFault(bridgeIds: string[] | undefined): OpvFault | null {
  const aimed = (bridgeIds ?? [])[0];
  if (!aimed || !resolveBridge(aimed)) return 'no-machine';
  if (!bridgeConnectedReactive(aimed)) return 'offline';
  return null;
}

export function opvFaultLabel(f: OpvFault): string {
  return f === 'no-machine' ? 'pick computer' : 'offline';
}

/**
 * <=1 send in flight + 1 pending (last value wins), with a small floor
 * between sends, so a fast drag never backs up the wire on a slow bridge.
 * Same shape as the Mixer's `makeSendGuard`, which is proven on the rig.
 */
export interface SendThrottle { schedule(v: number): void; force(v: number): void; cleanup(): void }

export function makeSendThrottle(send: (v: number) => Promise<unknown>): SendThrottle {
  let inFlight = false;
  let pending: number | null = null;
  let timer: number | undefined;
  const MIN_SEND_MS = 30;
  function fire(): void {
    timer = undefined;
    if (inFlight || pending === null) return;
    const v = pending; pending = null; inFlight = true;
    void send(v).finally(() => {
      inFlight = false;
      if (pending !== null && timer === undefined) timer = window.setTimeout(fire, MIN_SEND_MS);
    });
  }
  return {
    schedule(v) { pending = v; if (inFlight || timer !== undefined) return; fire(); },
    force(v) { pending = v; if (timer !== undefined) { clearTimeout(timer); timer = undefined; } if (!inFlight) fire(); },
    cleanup() { if (timer !== undefined) clearTimeout(timer); },
  };
}
